import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  InstoreBasketItem,
  InstoreBasketState,
} from "../../models/instoreBasket";
import { InstoreTemplateRow } from "../../models/instoreTemplate";
import { Store } from "../../models/store";

const initialState: InstoreBasketState = {
  template_id: null,
  store: null,
  items: [],
};

interface AddItemPayload {
  template_id: number;
  row: InstoreTemplateRow;
  quantity?: number;
}

interface SetQuantityPayload {
  template_row_id: number;
  quantity: number;
}

export const instoreBasketSlice = createSlice({
  name: "instoreBasket",
  initialState,
  reducers: {
    addInstoreBasketItem: (state, action: PayloadAction<AddItemPayload>) => {
      const { template_id, row, quantity = 1 } = action.payload;

      // Basket is bound to one template — reject items from a different one.
      if (state.template_id !== null && state.template_id !== template_id) {
        toast.error(
          "Please finish the current instore order before starting another."
        );
        return;
      }

      if (state.template_id === null) {
        state.template_id = template_id;
      }

      const existing = state.items.find(
        (item) => item.template_row_id === row.id
      );

      if (existing) {
        existing.quantity += quantity;
      } else {
        const newItem: InstoreBasketItem = {
          template_id,
          template_row_id: row.id,
          theme: row.theme,
          theme_id: row.theme_id,
          store_format: row.store_format,
          store_format_id: row.store_format_id,
          article_name: row.article_name,
          custom_fields: row.custom_fields,
          template_amount: row.amount,
          quantity,
        };
        state.items.push(newItem);
      }
    },
    setInstoreBasketItemQuantity: (
      state,
      action: PayloadAction<SetQuantityPayload>
    ) => {
      const { template_row_id, quantity } = action.payload;
      const item = state.items.find(
        (i) => i.template_row_id === template_row_id
      );
      if (!item) return;

      if (quantity <= 0) {
        state.items = state.items.filter(
          (i) => i.template_row_id !== template_row_id
        );
        if (state.items.length === 0) state.template_id = null;
      } else {
        item.quantity = quantity;
      }
    },
    removeInstoreBasketItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(
        (i) => i.template_row_id !== action.payload
      );
      if (state.items.length === 0) state.template_id = null;
    },
    setInstoreBasketStore: (state, action: PayloadAction<Store | null>) => {
      state.store = action.payload;
    },
    clearInstoreBasket: () => initialState,
  },
});

export const {
  addInstoreBasketItem,
  setInstoreBasketItemQuantity,
  removeInstoreBasketItem,
  setInstoreBasketStore,
  clearInstoreBasket,
} = instoreBasketSlice.actions;
